import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, UserCircle } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AnimatedBadgeCount } from './ui/AnimatedBadgeCount';
import { useReadEventApiV1EventsEventIdGet } from '@api-client/api-client';
import { getProfilePath } from '@utils/slugHelpers';
import { cn } from '@lib/utils';
import { useAuth } from '@hooks/useAuth';
import { useBellBadgeCount } from '@hooks/useBellBadgeCount';
import { useHelperFeedEnabled } from '@hooks/useHelperFeedEnabled';
import { useNotificationCountValue } from '@hooks/useNotificationCount';
import { useHelperFeedCountValue } from '@hooks/useHelperFeedCount';
import { HELPER_FEED_RETURN_TO } from '@components/helper-feed/constants';

// Custom SVG icon component for notifications
const NotificationsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path
      d="M17.7701 13.21L15.9701 11.4V6.94002C15.994 5.28384 15.4179 3.67493 14.3482 2.41035C13.2785 1.14578 11.7873 0.310951 10.1501 0.0600163C9.19989 -0.0651165 8.23394 0.0138312 7.31668 0.291592C6.39942 0.569352 5.55194 1.03954 4.8308 1.67078C4.10965 2.30202 3.53142 3.07979 3.13468 3.95221C2.73794 4.82463 2.53182 5.77163 2.53007 6.73002V11.4L0.730073 13.21C0.503979 13.4399 0.350657 13.7313 0.289273 14.0478C0.22789 14.3643 0.261169 14.6919 0.384951 14.9897C0.508732 15.2874 0.717524 15.542 0.985224 15.7217C1.25292 15.9014 1.56766 15.9982 1.89007 16H5.25007V16.34C5.29678 17.3552 5.74403 18.3105 6.4938 18.9965C7.24357 19.6826 8.23473 20.0434 9.25007 20C10.2654 20.0434 11.2566 19.6826 12.0063 18.9965C12.7561 18.3105 13.2034 17.3552 13.2501 16.34V16H16.6101C16.9325 15.9982 17.2472 15.9014 17.5149 15.7217C17.7826 15.542 17.9914 15.2874 18.1152 14.9897C18.239 14.6919 18.2723 14.3643 18.2109 14.0478C18.1495 13.7313 17.9962 13.4399 17.7701 13.21ZM11.2501 16.34C11.1946 16.821 10.9557 17.2621 10.5831 17.5713C10.2106 17.8805 9.73306 18.0341 9.25007 18C8.76709 18.0341 8.28957 17.8805 7.917 17.5713C7.54443 17.2621 7.30553 16.821 7.25007 16.34V16H11.2501V16.34Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * User info for avatar display
 */
interface UserInfo {
  id: string;
  slug?: string | null;
  profile_picture?: string | null;
  full_name?: string | null;
}

/**
 * Header props
 */
interface HeaderProps {
  /** Current user info (null for guests) */
  user?: UserInfo | null;
  /** Cached user info used during auth loading */
  cachedUserInfo?: Pick<UserInfo, 'profile_picture' | 'full_name'> | null;
  /** Callback when avatar is clicked (for guests, triggers login prompt) */
  onAvatarClick?: () => void;
  /** Callback when notifications button is clicked */
  onNotificationsClick?: () => void;
}

/**
 * Header component
 *
 * Reads `showAuthenticatedUI` from `useAuth()` to gate avatar / bell display.
 * Count polling (notifications + helpable) is owned by BadgeProvider (mounted at
 * RootLayout); Header just subscribes to the resulting caches via
 * `useBellBadgeCount`, which sums them — no second polling timer.
 */
export const Header: React.FC<HeaderProps> = ({
  user,
  cachedUserInfo,
  onAvatarClick,
  onNotificationsClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { showAuthenticatedUI } = useAuth();
  // Helper-feed gate (single source of truth): admins (has_extra_options) or the
  // launched flag. Drives whether the bell can land on the "You can help" tab.
  const helperFeedEnabled = useHelperFeedEnabled();
  // Combined bell count: unread notifications + unseen helpable (Dexter: users
  // must see the helper feed). BadgeProvider owns both polling lifecycles;
  // this hook just reads the two caches and sums them.
  const bellBadgeCount = useBellBadgeCount();

  // Bell target: the two counts read SEPARATELY (same skipToken subscribers the
  // badge sum uses, so no extra fetch) decide which notifications tab to open.
  // Land on the "You can help" tab only when the helper feed is on, Activity has
  // nothing unread, and there are unseen helpable questions; otherwise plain
  // notifications. Pure render derivation (no effect) so it stays compiler-clean.
  const { data: notificationCount } = useNotificationCountValue();
  const { data: helpableCount } = useHelperFeedCountValue();
  const notificationUnread = notificationCount?.unread_count ?? 0;
  const helpableUnseen = helpableCount?.unseen_count ?? 0;
  const goToHelpTab = helperFeedEnabled && notificationUnread === 0 && helpableUnseen > 0;
  const bellTarget = goToHelpTab ? HELPER_FEED_RETURN_TO : '/notifications';

  // Check if we're in edit mode and extract event ID
  const path = location.pathname;
  const editRouteMatch = /^\/events\/([^/]+)\/edit$/.exec(path);
  const eventId = editRouteMatch?.[1] ?? null;

  // Fetch event data when in edit mode (hook has built-in enabled: !!eventId)
  const { data: eventData } = useReadEventApiV1EventsEventIdGet(eventId || '');

  // Determine header configuration based on route
  // AIDEV-TODO: Instead of having this switch-case style code for setting the properties values here (title, showSearch, etc), have these be set by the code that uses this Header component abstraction, where the Layout is used; Consider abstracting the, currently, independent Header components and consolidating them into one that offers the flexibility to behave and be able to take the different properties and current design of each one (example: ProfileHeader, ChatHeader, GuestQuestionHeader, and this file - Header)
  const getHeaderConfig = () => {
    if (path.includes('/user/')) {
      return {
        showBackButton: false, // ProfilePage handles its own header
        title: null,
        showSearch: false,
        showAvatar: false,
      };
    }

    if (path.includes('/profile/') && path !== '/profile') {
      return {
        showBackButton: true,
        title: 'Profile',
        showSearch: false,
        showAvatar: false,
      };
    }

    // Handle edit mode - show event name instead of "Events"
    if (editRouteMatch && eventData?.data) {
      return {
        showBackButton: true,
        title: eventData.data.name,
        showSearch: false,
        showAvatar: false,
      };
    }

    // Exact /events path → homepage-style header (avatar + logo + notifications)
    if (path === '/events') {
      return {
        showBackButton: false,
        title: null,
        showSearch: true,
        showAvatar: true,
      };
    }

    // Sub-routes like /events/:id → back button
    if (path.includes('/events')) {
      return {
        showBackButton: true,
        title: 'Spaces',
        showSearch: false,
        showAvatar: false,
      };
    }

    if (path.includes('/messages/') && path !== '/messages') {
      return {
        showBackButton: true,
        title: 'Chat',
        showSearch: false,
        showAvatar: false,
      };
    }

    // Default header for main screens
    return {
      showBackButton: false,
      title: null,
      showSearch: true,
      showAvatar: true,
    };
  };

  const config = getHeaderConfig();
  const isNotificationsRoute = path.startsWith('/notifications');
  // Use real user data if available, fall back to cached data during auth loading, null for guests
  const avatarUser = user ?? cachedUserInfo ?? null;

  const handleAvatarClick = () => {
    if (onAvatarClick) {
      onAvatarClick();
      return;
    }

    // Default behavior: navigate to profile if authenticated
    if (showAuthenticatedUI && user?.id) {
      navigate(getProfilePath({ id: user.id, slug: user.slug ?? undefined }));
    }
  };

  const handleAvatarKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleAvatarClick();
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleNotificationsClick = () => {
    if (onNotificationsClick) {
      onNotificationsClick();
    } else {
      navigate(bellTarget);
    }
  };

  const renderLeftSection = () => {
    if (config.showBackButton) {
      return (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
          className="w-12 h-12 rounded-full p-0"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </Button>
      );
    }

    if (config.showAvatar) {
      return (
        <button
          className="rounded-full focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
          onClick={handleAvatarClick}
          onKeyDown={handleAvatarKeyDown}
          aria-label={showAuthenticatedUI ? 'Go to profile page' : 'Sign in'}
          type="button"
        >
          {showAuthenticatedUI && avatarUser ? (
            <UserAvatar
              imageUrl={avatarUser.profile_picture}
              fullName={avatarUser.full_name || 'O'}
              className="w-[42px] h-[42px] cursor-pointer"
              size="lg"
              alt="Profile"
            />
          ) : (
            <div className="w-[42px] h-[42px] rounded-full bg-gray-200 flex items-center justify-center cursor-pointer">
              <UserCircle className="w-8 h-8 text-gray-500" />
            </div>
          )}
        </button>
      );
    }

    return <div className="w-[35px]" />;
  };

  const handleLogoClick = () => {
    // Only navigate if not already on home page
    if (location.pathname !== '/home') {
      navigate('/home');
    }
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className="flex w-full items-center justify-between pb-0 px-3.5"
      style={{ height: 'var(--header-content-height)' }}
    >
      {/* Left side */}
      {renderLeftSection()}

      {/* Center */}
      {config.title ? (
        <h1 className="text-lg font-semibold text-black">{config.title}</h1>
      ) : (
        <button
          className="focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2 rounded-lg transition-all"
          aria-label="Go to home page"
          type="button"
        >
          <img
            className="relative w-[320px] h-16"
            alt="Meetball Logo"
            src="/Meetball Logo.svg"
            onClick={handleLogoClick}
            fetchPriority="high"
          />
        </button>
      )}

      {/* Right side */}
      {config.showSearch ? (
        <div className="flex items-center gap-2">
          {showAuthenticatedUI ? (
            <div className="relative" onClick={handleNotificationsClick}>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'rounded-full p-0 [&_svg]:size-5',
                  isNotificationsRoute &&
                    'bg-mb-yellow-soft text-black shadow-xs hover:bg-mb-yellow-soft hover:text-black',
                )}
                title="Notifications"
              >
                <NotificationsIcon />
              </Button>

              {/* Badge for combined unread count (notifications + helpable).
                  The number itself is an AnimatedBadgeCount: it pops on increment
                  and reveals on first paint, while the Badge keeps owning the
                  red pill's color / position / hide-at-0. max=99 preserves the
                  existing 99+ cap. */}
              {bellBadgeCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute rounded-xl top-1 right-[2px] h-4 w-4 text-xs p-0 flex items-center justify-center z-20 select-none"
                >
                  <AnimatedBadgeCount count={bellBadgeCount} max={99} className="leading-none" />
                </Badge>
              )}
            </div>
          ) : (
            <div className="w-[46px]" />
          )}
        </div>
      ) : (
        <div className="w-[46px]" />
      )}
    </header>
  );
};
